import { isAxiosError } from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";

/// A3: the ingress cap, restated for the editor's footer fact.
export const ATTACHMENT_MAX_FILE_BYTES = 100 * 1024 * 1024;

/// Mirrors `common_core::attachment::AttachmentDescriptor` on the wire.
export interface StoredDescriptor {
  kind: "stored";
  sha256: string;
  size: number;
  content_type: string;
  filename: string;
  uploaded_by: string;
  uploaded_at: string;
}

export interface ExternalDescriptor {
  kind: "external";
  url: string;
  filename?: string;
  content_type?: string;
  size?: number;
  added_by: string;
  added_at: string;
}

export type AttachmentDescriptor = StoredDescriptor | ExternalDescriptor;

/// `null` for a cell entry that is not a descriptor; the row still lists it
/// under its raw text so a corrupt cell is visible, never blank.
export function parseDescriptor(raw: string): AttachmentDescriptor | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "kind" in parsed &&
      ((parsed as { kind: unknown }).kind === "stored" ||
        (parsed as { kind: unknown }).kind === "external")
    ) {
      return parsed as AttachmentDescriptor;
    }
  } catch {
    /* fall through */
  }
  return null;
}

/// The filename, or the URL for a link without one (A8) — the same rule the
/// canvas chip and every export follow.
export function displayName(d: AttachmentDescriptor | null, raw: string) {
  if (!d) return raw;
  return d.kind === "stored" ? d.filename : (d.filename ?? d.url);
}

/// Upper-cased extension, `FILE` when there is none — the Rust `badge()` rule,
/// so the canvas, the panes and an upload still in flight all agree.
export function badgeForFilename(filename: string): string {
  const dot = filename.lastIndexOf(".");
  const ext = dot >= 0 ? filename.slice(dot + 1) : "";
  return ext.length > 0 && ext.length <= 5 ? ext.toUpperCase() : "FILE";
}

/// As `badgeForFilename`, plus `URL` for a link.
export function badgeFor(d: AttachmentDescriptor | null): string {
  if (!d) return "FILE";
  if (d.kind === "external") return "URL";
  return badgeForFilename(d.filename);
}

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/// A minified external descriptor, the shape the door validates (A7).
export function externalDescriptor(url: string, addedBy: string): string {
  const d: ExternalDescriptor = {
    kind: "external",
    url,
    added_by: addedBy,
    added_at: new Date().toISOString(),
  };
  return JSON.stringify(d);
}

const doorPath = (tableId: string, sha256?: string) =>
  `/api/tables/${encodeURIComponent(tableId)}/attachments${sha256 ? `/${sha256}` : ""}`;

/// The download door, with the descriptor's own name riding along: one object
/// may sit behind many names, so the door cannot know which one to hand back.
export function attachmentUrl(
  tableId: string,
  sha256: string,
  filename: string,
): string {
  const base = axiosInstance.defaults.baseURL ?? "";
  return `${base}${doorPath(tableId, sha256)}?filename=${encodeURIComponent(filename)}`;
}

export interface UploadResponse {
  descriptor: string;
  sha256: string;
  size: number;
  content_type: string;
  filename: string;
  deduplicated: boolean;
}

/// The cap as the footer and the refusal both say it.
export const ATTACHMENT_MAX_FILE_LABEL = `${ATTACHMENT_MAX_FILE_BYTES / (1024 * 1024)} MB`;

const ERROR_MESSAGES: Record<string, string> = {
  storage_quota_exceeded: "Storage quota exceeded",
  attachment_too_large: `Files are capped at ${ATTACHMENT_MAX_FILE_LABEL}`,
  attachments_unbilled_table: "This table cannot hold files",
  attachment_gone: "File no longer available",
  attachment_not_found: "File not found",
  invalid_filename: "That filename cannot be stored",
  empty_file: "The file is empty",
};

export function attachmentErrorMessage(
  status: number,
  body: unknown,
  fallback: string,
): string {
  const code =
    typeof body === "object" && body !== null && "error" in body
      ? String((body as { error: unknown }).error)
      : null;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (status === 403) return "You cannot edit this table";
  return fallback;
}

/// Upload one file through the table's door. The multipart header is explicit
/// because the instance defaults to JSON, and axios serialises a FormData body
/// as JSON under that content type; the browser then sets the boundary.
export async function uploadAttachment(
  tableId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  try {
    const { data } = await axiosInstance.post<UploadResponse>(
      doorPath(tableId),
      form,
      {
        signal,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total && onProgress) onProgress(e.loaded / e.total);
        },
      },
    );
    return data;
  } catch (error) {
    if (signal?.aborted)
      throw new DOMException("Upload cancelled", "AbortError");
    if (isAxiosError(error) && error.response) {
      throw new Error(
        attachmentErrorMessage(
          error.response.status,
          error.response.data,
          "Upload failed",
        ),
      );
    }
    throw new Error("Upload failed");
  }
}

export interface HoldingTable {
  table_id: string;
  name: string;
  /// Cells of that table holding the file; null when the scan could not answer.
  cells: number | null;
}

/// The two 409 bodies of the delete door (docs/Attachments.md § Delete file now).
export type DeleteRefusal =
  | {
      kind: "referenced_elsewhere";
      tables: HoldingTable[];
      hidden_count: number;
    }
  | { kind: "in_other_cells"; cells: number };

export type DeleteOutcome =
  { deleted: true; bytes: number } | { deleted: false; refusal: DeleteRefusal };

export async function deleteAttachment(
  tableId: string,
  sha256: string,
): Promise<DeleteOutcome> {
  try {
    const { data } = await axiosInstance.delete<{ bytes: number }>(
      doorPath(tableId, sha256),
    );
    return { deleted: true, bytes: data.bytes };
  } catch (error) {
    if (!isAxiosError(error) || !error.response)
      throw new Error("Delete failed");
    const { status, data } = error.response;
    if (status === 409) {
      const body = data as
        | {
            error: "attachment_referenced_elsewhere";
            tables: HoldingTable[];
            hidden_count: number;
          }
        | { error: "attachment_in_other_cells"; cells: number };
      if (body.error === "attachment_referenced_elsewhere") {
        const { tables, hidden_count } = body;
        return {
          deleted: false,
          refusal: { kind: "referenced_elsewhere", tables, hidden_count },
        };
      }
      if (body.error === "attachment_in_other_cells") {
        return {
          deleted: false,
          refusal: { kind: "in_other_cells", cells: body.cells },
        };
      }
    }
    throw new Error(attachmentErrorMessage(status, data, "Delete failed"));
  }
}

/// Saves `href` under `filename` through a plain anchor click.
export function saveAs(href: string, filename: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/// Download is a navigation to the door: it answers `Content-Disposition:
/// attachment`, so a plain anchor click saves the file.
export function downloadAttachment(tableId: string, d: StoredDescriptor) {
  saveAs(attachmentUrl(tableId, d.sha256, d.filename), d.filename);
}

/// The only files the viewer renders (A2): passive raster images, and PDFs drawn
/// to canvas. The door names one of these only when the bytes prove it, so a
/// fetched blob's `type` is the one to trust; the descriptor's `content_type` is
/// the uploader's claim.
export type PreviewKind = "image" | "pdf";
const PREVIEW_KINDS = new Map<string, PreviewKind>([
  ["image/png", "image"],
  ["image/jpeg", "image"],
  ["image/gif", "image"],
  ["image/webp", "image"],
  ["image/avif", "image"],
  ["application/pdf", "pdf"],
]);

// ponytail: bounds bytes, not pixels — a small file can still decode huge; read
// the image header's dimensions before decoding if that ever takes a tab down.
export const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;

export const previewKind = (type: string): PreviewKind | null =>
  PREVIEW_KINDS.get(type.split(";")[0].trim().toLowerCase()) ?? null;

/// Worth a request: the uploader claimed a previewable file small enough to show.
export const mayPreview = (d: StoredDescriptor): boolean =>
  previewKind(d.content_type) !== null && d.size <= PREVIEW_MAX_BYTES;

export type AttachmentFetch =
  { blob: Blob } | { status: number; message?: string };

/// The file's bytes for the viewer. A refusal comes back as the door's status and
/// its own `message` (the egress 429 has one); the body is a Blob here.
export async function fetchAttachmentBlob(
  tableId: string,
  d: StoredDescriptor,
  signal: AbortSignal,
): Promise<AttachmentFetch> {
  try {
    const response = await axiosInstance.get<Blob>(
      doorPath(tableId, d.sha256),
      {
        // The export log records every fetch and folds previews away.
        params: { filename: d.filename, purpose: "preview" },
        responseType: "blob",
        signal,
      },
    );
    return { blob: response.data };
  } catch (error) {
    if (!isAxiosError(error) || !error.response) throw error;
    const { status, data } = error.response;
    let message: string | undefined;
    try {
      const m: unknown = JSON.parse(await (data as Blob).text())?.message;
      if (typeof m === "string") message = m;
    } catch {
      /* no JSON body */
    }
    return { status, message };
  }
}

/// Open a link in a new tab, or download a stored file — the canvas editor's
/// row action; the row section opens a stored file in the viewer instead.
export function openAttachment(tableId: string, d: AttachmentDescriptor) {
  if (d.kind === "external")
    window.open(d.url, "_blank", "noopener,noreferrer");
  else downloadAttachment(tableId, d);
}

/// What "Copy link" puts on the clipboard: the door URL for a stored file, the
/// link itself for an external one.
export function linkFor(tableId: string, d: AttachmentDescriptor): string {
  if (d.kind === "external") return d.url;
  return new URL(
    attachmentUrl(tableId, d.sha256, d.filename),
    window.location.origin,
  ).href;
}
